import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ShieldCheck } from "lucide-react";

const serial = (orderId, productId, i) =>
  `TL-${orderId.replace(/^TL/, "")}-${productId.slice(-4).toUpperCase()}-${String(i + 1).padStart(2, "0")}`;

const Card = ({ order, item, index }) => (
  <article data-testid={`certificate-card-${item.product_id}`}
    className="certificate relative bg-luxa-espresso text-luxa-cream p-9 sm:p-12 overflow-hidden">
    <span aria-hidden="true" className="absolute inset-4 border border-luxa-gold/35 pointer-events-none" />
    <div className="relative">
      <div className="flex items-start justify-between gap-6">
        <div>
          <span aria-hidden="true" className="inline-flex items-center justify-center w-12 h-12 border border-luxa-gold/60 font-serif text-luxa-gold text-lg tracking-[0.1em]">TL</span>
          <p className="mt-5 font-serif text-2xl tracking-[0.2em]">TRUST LUXA</p>
          <p className="overline text-luxa-gold mt-2">Certificate of Authenticity</p>
        </div>
        <ShieldCheck size={26} strokeWidth={1.2} className="text-luxa-gold shrink-0" />
      </div>

      <div className="mt-10 grid sm:grid-cols-[1.4fr_1fr] gap-8 items-start">
        <div>
          <p className="overline text-luxa-cream/45">The piece</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl tracking-tight leading-tight">{item.name}</h2>
          <p className="mt-4 text-sm leading-relaxed text-luxa-cream/65 max-w-md">
            This object was cut from a single full-grain, vegetable-tanned hide and saddle-stitched by hand
            with waxed linen thread. It carries a ten-year repair promise from the bench that made it.
          </p>
        </div>
        <img src={item.image} alt={item.name} className="w-full aspect-[4/5] object-cover border border-luxa-gold/25" />
      </div>

      <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-luxa-gold/25 pt-7">
        {[["Serial", serial(order.id, item.product_id, index)],
          ["Order", order.id],
          ["Issued", (order.created_at || "").slice(0, 10)],
          ["Owner", order.address?.full_name]].map(([l, v]) => (
          <div key={l}>
            <p className="overline text-luxa-gold">{l}</p>
            <p className="mt-2 font-serif text-lg break-words">{v || "—"}</p>
          </div>
        ))}
      </div>

      <p className="mt-9 text-xs tracking-[0.18em] uppercase text-luxa-cream/40">
        Verify at trustluxa.com/track · {serial(order.id, item.product_id, index)}
      </p>
    </div>
  </article>
);

export default function Certificate() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/track/${orderId}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
  }, [orderId]);

  if (order === false) return <div className="py-32 text-center font-serif text-2xl">No order found with that number.</div>;
  if (!order) return <div className="py-32 text-center overline text-luxa-espresso/40">Loading…</div>;

  const cards = order.items.flatMap((item, i) =>
    Array.from({ length: item.quantity }, (_, k) => ({ item, index: i * 10 + k })));

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16" data-testid="certificate-page">
      <div className="flex flex-wrap items-end justify-between gap-5 no-print">
        <div>
          <p className="overline text-luxa-brown flex items-center gap-3">
            <span className="w-8 h-px bg-luxa-gold" aria-hidden="true" />Authenticity
          </p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tighter leading-none">Your certificates</h1>
          <p className="mt-4 text-luxa-espresso/60 max-w-lg">
            One card per piece in order {order.id}, matching the gold-foiled card in your packaging. Print it, or keep it here.
          </p>
        </div>
        <Button data-testid="print-certificate" onClick={() => window.print()}
          className="rounded-none bg-luxa-brown hover:bg-luxa-brown/90 text-luxa-cream h-12 px-8 overline">
          <Printer size={14} strokeWidth={1.5} className="mr-2" />Print
        </Button>
      </div>

      <div className="mt-12 space-y-10">
        {cards.map(({ item, index }) => <Card key={`${item.product_id}-${index}`} order={order} item={item} index={index} />)}
      </div>

      <Link to={`/track?order=${order.id}`} className="mt-12 inline-block overline text-luxa-brown link-underline no-print">
        ← Back to order tracking
      </Link>
    </div>
  );
}
